import { formatCents, formatMicro, type Micro } from "@split/core";

export { formatCents, formatMicro };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-12" -> "Oct 12" (adds the year when it isn't this year). */
export function shortDate(iso: string, thisYear = new Date().getFullYear()): string {
  const y = Number(iso.slice(0, 4));
  const m = MONTHS[Number(iso.slice(5, 7)) - 1] ?? "";
  const d = Number(iso.slice(8, 10));
  return y === thisYear ? `${m} ${d}` : `${m} ${d}, ${y}`;
}

/** "2026-10-13T16:43" -> "Oct 13, 16:43". Also accepts full ISO timestamps (shown in local time). */
export function shortDateTime(iso: string): string {
  let local = iso;
  if (/Z$|[+-]\d{2}:\d{2}$/.test(iso)) {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  // 24-hour time.
  return `${shortDate(local.slice(0, 10))}, ${local.slice(11, 16)}`;
}

export function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${today()}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Money without the cents when they're zero, for compact chart labels. */
export function compactMicro(m: Micro): string {
  const s = formatMicro(m);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
}

/** Reads what a person types into a money field: "12", "12.5", "$1,200.00", "-3". */
export function readMoney(text: string): number | null {
  const t = text.trim().replace(/[$,\s]/g, "");
  if (!/^-?\d*(\.\d{0,2})?$/.test(t) || t === "" || t === "-" || t === ".") return null;
  const neg = t.startsWith("-");
  const [w = "0", f = ""] = t.replace("-", "").split(".");
  const cents = Number(w || "0") * 100 + Number(f.padEnd(2, "0"));
  return neg ? -cents : cents;
}

/** "7.75" or "7.75%" -> 775 basis points. */
export function readPercent(text: string): number | null {
  const t = text.trim().replace(/%$/, "").trim();
  if (!/^\d{1,3}(\.\d{0,2})?$/.test(t)) return null;
  const [w = "0", f = ""] = t.split(".");
  const bp = Number(w) * 100 + Number(f.padEnd(2, "0"));
  return bp <= 10_000 ? bp : null;
}

export function centsInput(c: number): string {
  if (c === 0) return "";
  const s = (Math.abs(c) / 100).toFixed(2);
  return c < 0 ? `-${s}` : s;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? "?";
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
  return (a + b).toUpperCase().slice(0, 2);
}

/** "2026-10" -> "Oct 2026". */
export function monthLabel(ym: string): string {
  return `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
}

/** How a receipt is named on screen: "Costco", or "Costco, receipt 2" when a visit had several. */
export function receiptName(store: string, receipt: string | undefined): string {
  if (!receipt) return store;
  return /^\d+$/.test(receipt) ? `${store}, receipt ${receipt}` : `${store}, ${receipt}`;
}

/** "emily chen" or "EMILY CHEN" -> "Emily Chen"; anything with mixed capitals is left as typed. */
export function tidyName(raw: string): string {
  const t = raw.trim().replace(/\s+/g, " ");
  if (t !== t.toLowerCase() && t !== t.toUpperCase()) return t;
  return t
    .toLowerCase()
    .replace(
      /(^|[\s\-'])(\p{L})/gu,
      (_m: string, sep: string, ch: string) => sep + ch.toUpperCase(),
    );
}

/** How to show someone's sign-in name: their name when it matches, else the name capitalized. */
export function signInName(name: string, username: string | null): string | null {
  if (!username) return null;
  return name.trim().replace(/\s+/g, " ").toLowerCase() === username ? name : tidyName(username);
}

/**
 * Short names for tight spots (ledger columns, tick buttons): first names, with the last
 * initial when two people share a first name ("Emily C.", "Emily W.").
 */
export function shortNames(
  people: readonly { id: string; name: string }[],
): Record<string, string> {
  const firsts = new Map<string, number>();
  for (const p of people) {
    const f = p.name.trim().split(/\s+/)[0]!.toLowerCase();
    firsts.set(f, (firsts.get(f) ?? 0) + 1);
  }
  const out: Record<string, string> = {};
  for (const p of people) {
    const parts = p.name.trim().split(/\s+/);
    const first = parts[0] ?? p.name;
    out[p.id] =
      (firsts.get(first.toLowerCase()) ?? 0) > 1 && parts.length > 1
        ? `${first} ${parts.at(-1)![0]}.`
        : first;
  }
  return out;
}
